import React, { useState, useCallback } from 'react';
import { Upload, Send, AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const SubmissionForm = () => {
    const [formData, setFormData] = useState({
        type: 'testimonial',
        author: '',
        relationship: '',
        content: '',
        photos: [],
        tags: ''
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus({ type: '', message: '' });

        try {
            // Handle photo uploads first if any
            let photoUrls = [];
            if (formData.photos.length > 0) {
                const uploadPromises = Array.from(formData.photos).map(async (photo) => {
                    const formDataWithPhoto = new FormData();
                    formDataWithPhoto.append('file', photo);
                    const response = await fetch('/api/upload-photo', {
                        method: 'POST',
                        body: formDataWithPhoto
                    });
                    if (!response.ok) throw new Error('Photo upload failed');
                    const data = await response.json();
                    return data.url;
                });

                photoUrls = await Promise.all(uploadPromises);
            }

            // Submit the form data
            const submission = {
                ...formData,
                photoUrls,
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean)
            };

            const response = await fetch('/api/submit-memory', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submission)
            });

            if (!response.ok) throw new Error('Submission failed');

            setStatus({
                type: 'success',
                message: 'Thank you for sharing your memory. It will be reviewed and posted shortly.'
            });
            setFormData({
                type: 'testimonial',
                author: '',
                relationship: '',
                content: '',
                photos: [],
                tags: ''
            });
        } catch (error) {
            setStatus({
                type: 'error',
                message: 'There was an error submitting your memory. Please try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setFormData(prev => ({ ...prev, photos: files }));
    };

    return (
        <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
                {status.message && (
                    <Alert variant={status.type === 'error' ? 'destructive' : 'default'}>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{status.message}</AlertDescription>
                    </Alert>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Type of Submission
                        </label>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                            className="w-full p-2 border rounded-lg"
                            required
                        >
                            <option value="testimonial">Written Memory</option>
                            <option value="photo">Photo with Caption</option>
                            <option value="both">Both</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Your Name
                        </label>
                        <input
                            type="text"
                            value={formData.author}
                            onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                            className="w-full p-2 border rounded-lg"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Relationship to Gareth
                        </label>
                        <input
                            type="text"
                            value={formData.relationship}
                            onChange={(e) => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                            className="w-full p-2 border rounded-lg"
                            required
                            placeholder="e.g., Colleague, Friend, Student"
                        />
                    </div>

                    {(formData.type === 'testimonial' || formData.type === 'both') && (
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Your Memory
                            </label>
                            <textarea
                                value={formData.content}
                                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                                className="w-full p-2 border rounded-lg"
                                rows="6"
                                required={formData.type !== 'photo'}
                                placeholder="Share your memory of Gareth..."
                            />
                        </div>
                    )}

                    {(formData.type === 'photo' || formData.type === 'both') && (
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Photos
                            </label>
                            <div className="flex items-center justify-center w-full">
                                <label className="w-full flex flex-col items-center px-4 py-6 bg-white text-primary rounded-lg shadow-lg tracking-wide border border-primary cursor-pointer hover:bg-primary hover:text-white">
                                    <Upload className="w-8 h-8" />
                                    <span className="mt-2 text-base">Select photo(s)</span>
                                    <input
                                        type="file"
                                        className="hidden"
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        required={formData.type === 'photo'}
                                    />
                                </label>
                            </div>
                            {formData.photos.length > 0 && (
                                <div className="mt-2 text-sm text-gray-600">
                                    {formData.photos.length} file(s) selected
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Tags
                        </label>
                        <input
                            type="text"
                            value={formData.tags}
                            onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                            className="w-full p-2 border rounded-lg"
                            placeholder="e.g., mountaineering, academic, friendship (comma separated)"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-white p-3 rounded-lg hover:bg-primary-light transition-colors disabled:opacity-50"
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Submitting...
                        </>
                    ) : (
                        <>
                            <Send className="w-4 h-4" />
                            Share Memory
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};

export default SubmissionForm;